import { useEffect, useMemo } from 'react';
import Modal from 'react-bootstrap/Modal';
import DOMPurify from 'dompurify';

import { setZIndexForModalForms } from '../../../lib/functions';

import './Drawer.scss';

export interface DrawerProps {
  /** Показана ли шторка. */
  show: boolean;
  /** Закрытие: Escape / клик по фону / крестик. */
  onHide: () => void;
  /** Заголовок шторки (aria-labelledby). */
  title?: React.ReactNode;
  /** Сторона выезжания. По умолчанию 'right'. */
  placement?: 'right' | 'left';
  /**
   * Backdrop: true — клик по фону закрывает (по умолчанию),
   * 'static' — фон не закрывает, false — без фона.
   */
  backdrop?: boolean | 'static';
  /** Escape закрывает шторку. По умолчанию true. */
  keyboard?: boolean;
  /** Доп. класс на dialog. */
  className?: string;
  /** Доп. класс на body (для html-вставки/прокрутки). */
  bodyClassName?: string;
  /**
   * HTML-фрагмент с бека (например, текст инструкции).
   * Если задан — санитизируется через DOMPurify и рендерится как HTML
   * (в этом случае children игнорируется). Иначе показывается children.
   */
  html?: string;
  /** Контент-нода. Показывается, когда html не задан. */
  children?: React.ReactNode;
}

/**
 * Конфиг санитизации: только безопасные теги/атрибуты. По образцу
 * ProductContentPreview.tsx — единая конвенция проекта для html-вставок.
 */
const SANITIZE_CONFIG: DOMPurify.Config = {
  ALLOWED_TAGS: [
    // Заголовки
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    // Текст
    'p', 'br', 'hr', 'span', 'div',
    // Форматирование
    'strong', 'b', 'em', 'i', 'u', 's', 'sub', 'sup', 'mark', 'small',
    // Списки
    'ul', 'ol', 'li',
    // Ссылки
    'a',
    // Цитаты/код
    'blockquote', 'pre', 'code',
    // Таблицы
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    // Изображения
    'img',
  ],
  ALLOWED_ATTR: [ 'href', 'target', 'rel', 'class', 'id', 'src', 'alt', 'title', 'style', 'colspan', 'rowspan' ],
  ALLOW_DATA_ATTR: false,
  ALLOW_UNKNOWN_PROTOCOLS: false,
};

/**
 * Правая/левая «шторка» — панель, выезжающая сбоку экрана.
 *
 * Построена на `react-bootstrap/Modal` (BS4) — это даёт из коробки:
 * портал в document.body, focus-trap, блокировку скролла body,
 * серый backdrop, закрытие по Escape и по клику на фон, возврат фокуса,
 * aria-modal/role=dialog. В отличие от BS5-Offcanvas, работает под Bootstrap 4.
 *
 * Ширина: 30vw на десктопе (≈576px на Full HD), 100vw на планшетах/мобилках.
 * Z-index синхронизирован с другими модалками через setZIndexForModalForms.
 *
 * Контент: по умолчанию рендерится children (как React-нода).
 * Если с бека приходит HTML-фрагмент — передайте его в проп `html`:
 * он санитизируется (DOMPurify, белый список тегов) и безопасно вставляется.
 * `html` имеет приоритет над children.
 */
const Drawer = ({
  show,
  onHide,
  title,
  placement = 'right',
  backdrop = true,
  keyboard = true,
  className,
  bodyClassName,
  html,
  children,
}: DrawerProps) => {
  // Подстраиваем z-index под общий стек оверлеев проекта (см. FormModal).
  useEffect(() => {
    if (show) {
      setZIndexForModalForms();
    }
  }, [ show ]);

  // Санитизируем html один раз на изменение значения — DOMPurify сравнительно
  // дорогой, нет смысла гонять его на каждый рендер.
  const sanitizedHtml = useMemo(
    () => (html ? DOMPurify.sanitize(html, SANITIZE_CONFIG) : undefined),
    [ html ]
  );

  if (!show) {
    return null;
  }

  const dialogClassName = [
    'core-drawer',
    placement === 'left' ? 'core-drawer--left' : 'core-drawer--right',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Modal
      show={ show }
      onHide={ onHide }
      backdrop={ backdrop }
      keyboard={ keyboard }
      animation
      dialogClassName={ dialogClassName }
      aria-labelledby={ title ? 'core-drawer-title' : undefined }
      // Контент шторки не центрируется модалкой — см. Drawer.scss.
      className='core-drawer-modal'
    >
      { title && (
        <Modal.Header closeButton>
          <Modal.Title id='core-drawer-title'>
            { title }
          </Modal.Title>
        </Modal.Header>
      ) }
      <Modal.Body className={ bodyClassName }>
        { sanitizedHtml !== undefined
          ? <div dangerouslySetInnerHTML={ { __html: sanitizedHtml } } />
          : children }
      </Modal.Body>
    </Modal>
  );
};

export default Drawer;
